import { createFileRoute, Link, useNavigate } from "@tanstack/solid-router";
import { createSignal, ErrorBoundary, Suspense } from "solid-js";
import { ProjectCard } from "~/components/projects/ProjectCard";
import { ProjectsSkeleton } from "~/components/projects/ProjectsSkeleton";
import {
  ProjectsViewToggle,
  type ViewMode,
} from "~/components/projects/ProjectsViewToggle";
import { ProjectTable } from "~/components/projects/ProjectTable";
import { ErrorCard } from "~/components/ui/ErrorCard";
import { useProjectsQuery } from "~/lib/queries/projects";

export const Route = createFileRoute("/_authenticated/projects/")({
  ssr: false,
  component: ProjectsPage,
});

function ProjectsContent() {
  const navigate = useNavigate();
  const [view, setView] = createSignal<ViewMode>("grid");
  const projectsQuery = useProjectsQuery();

  const projects = () => projectsQuery.data ?? [];

  const handleProjectClick = (projectId: string) => {
    const project = projects().find((p) => p.projectId === projectId);
    if (project && project.state === "READY") {
      navigate({ to: `/projects/${projectId}` });
    }
  };

  return (
    <div class="max-w-6xl mx-auto space-y-6">
      {/* Page header */}
      <div class="flex items-center justify-between gap-4">
        <div>
          <h1 class="text-xl font-semibold text-foreground">Projects</h1>
          <p class="text-sm text-muted-foreground mt-0.5">
            {projectsQuery.isSuccess
              ? `${projects().length} project${projects().length !== 1 ? "s" : ""}`
              : "Loading…"}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <ProjectsViewToggle view={view()} onChange={setView} />
          <Link
            to="/projects/new"
            mask={{ to: "/projects", unmaskOnReload: true }}
            class="inline-flex h-9 items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground
                   text-sm font-medium hover:bg-primary/90 transition-colors shrink-0
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span aria-hidden="true">+</span>
            New Project
          </Link>
        </div>
      </div>

      {/* Loading */}
      {projectsQuery.isPending && <ProjectsSkeleton />}

      {/* Error */}
      {projectsQuery.isError && (
        <ErrorCard
          message="Failed to load projects."
          action={
            <button
              type="button"
              onClick={() => projectsQuery.refetch()}
              class="inline-flex h-8 items-center px-3 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          }
        />
      )}

      {/* Empty state */}
      {projectsQuery.isSuccess && projects().length === 0 && (
        <div class="rounded-xl border border-dashed border-border p-16 text-center">
          <p class="text-sm text-muted-foreground mb-4">
            No projects yet. Get started by creating one.
          </p>
          <Link
            to="/projects/new"
            mask={{ to: "/projects", unmaskOnReload: true }}
            class="inline-flex h-9 items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground
                   text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <span aria-hidden="true">+</span>
            New Project
          </Link>
        </div>
      )}

      {/* Grid view */}
      {projectsQuery.isSuccess &&
        projects().length > 0 &&
        view() === "grid" && (
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects().map((project) => (
              <ProjectCard
                projectId={project.projectId}
                name={project.name}
                description={project.description}
                state={project.state}
              />
            ))}
          </div>
        )}

      {/* Table view */}
      {projectsQuery.isSuccess &&
        projects().length > 0 &&
        view() === "table" && (
          <ProjectTable
            projects={projects()}
            onProjectClick={handleProjectClick}
          />
        )}
    </div>
  );
}

function ProjectsPage() {
  return (
    <ErrorBoundary
      fallback={(err) => (
        <ErrorCard
          title="Projects error"
          message={err?.message ?? "An unexpected error occurred."}
        />
      )}
    >
      <Suspense fallback={<ProjectsSkeleton />}>
        <ProjectsContent />
      </Suspense>
    </ErrorBoundary>
  );
}
